import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { WorkerAttr } from '@/types/worker-attr';
import { getLiquidTemplate } from '../utils/get-liquid-template';
import { WORKER_ATTR_NAME } from '../utils/constants';
import WorkerAttributeNodeView from '../components/worker-attribute-node-view';
import { toDataAttrs } from '../utils/to-data-attrs';

type WorkerAttrNodeAttributes = {
  [K in keyof Required<WorkerAttr>]: {
    default: WorkerAttr[K];
  };
};

export const WorkerAttributeExtension = Node.create({
  name: WORKER_ATTR_NAME,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: '',
        ...toDataAttrs('id'),
      },
      label: {
        default: '',
        ...toDataAttrs('label'),
      },
      fallback: {
        default: '',
        ...toDataAttrs('fallback'),
      },
      tag: {
        default: 'worker',
        ...toDataAttrs('tag'),
      },
      dataType: {
        default: 'string',
        ...toDataAttrs('dataType'),
      },
      attrType: {
        default: undefined,
        ...toDataAttrs('attrType'),
      },
      useUtcDateFormatting: {
        default: false,
        ...toDataAttrs('useUtcDateFormatting'),
      },
    } satisfies WorkerAttrNodeAttributes;
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderText({ node }) {
    // Check if the node already has text content (workaround for TipTap bug)
    // See: https://github.com/ueberdosis/tiptap/pull/3410
    const hasTextAlready = node.content?.content?.length;
    if (hasTextAlready) {
      return '';
    }

    // Ensure we have valid attributes with fallbacks
    const tag = node.attrs.tag || 'worker';
    const id = node.attrs.id || '';
    const fallback = node.attrs.fallback || '';
    const dataType = node.attrs.dataType || 'string';

    return getLiquidTemplate(tag, id, fallback, dataType);
  },

  renderHTML({ HTMLAttributes }) {
    const template = getLiquidTemplate(
      HTMLAttributes['data-tag'],
      HTMLAttributes['data-id'],
      HTMLAttributes['data-fallback'],
      HTMLAttributes['data-dataType'],
    );

    return [
      'span',
      mergeAttributes(
        { 'data-type': this.name },
        {
          'data-id': HTMLAttributes['data-id'],
          'data-label': HTMLAttributes['data-label'],
          'data-fallback': HTMLAttributes['data-fallback'],
          'data-tag': HTMLAttributes['data-tag'],
          'data-dataType': HTMLAttributes['data-dataType'],
          class: 'mx-[3px] rounded-md px-[4px] py-[1px] bg-blue-50 text-blue-700 text-sm',
        },
      ),
      template,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(WorkerAttributeNodeView);
  },
});

